import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  ButtonBase,
  CardMedia,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Grid2 as Grid,
  useTheme,
  FormHelperText,
  Alert,
} from '@mui/material';
import { StorageReference } from 'firebase/storage';
import LinearProgress from '@mui/material/LinearProgress';
import pluralize from 'pluralize';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  deleteOrganizationImages,
  fetchOrganizationImages,
  imagesByOrganizationId,
  uploadOrganizationImage,
} from '@/features/organizations/slice';
import {
  fetchTemplateImages,
  imagesByTemplateId,
  uploadTemplateImage,
} from '@/features/admin/templates/slice';
import { ArrowLeft, Trash2, Trees, Upload, UploadIcon, X } from 'lucide-react';
import { useAccessControl } from '@/features/access-control';
import useSnackbar from '@/hooks/useSnackbar';
import './MediaManager.scss';
import { ONE_MB, TWO_MB } from '@/constants/global';
import './MediaManager.scss';

export interface Metadata {
  name: string;
  size: number;
  timeCreated: string;
}

const MAX_FILE_SIZE = TWO_MB;

export interface Media extends Metadata {
  src: string;
  fullPath: string;
  ref?: StorageReference;
  updated: string;
  contentType: string;
}

const MAX_FILES = 75;

interface Props {
  value?: string;
  multiple?: boolean;
  helperText?: string;
  onChange: (selection: string[]) => void;
  onDelete?: () => void;
}

const MediaManager = ({
  value,
  multiple = false,
  helperText,
  onChange,
  onDelete,
}: Props) => {
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const { hasAccess } = useAccessControl();
  const [openSnackbar] = useSnackbar();

  const { organizationId, templateId } = useParams<{
    organizationId?: string;
    templateId?: string;
  }>();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const organizationImages = useAppSelector(
    imagesByOrganizationId(organizationId)
  ) as string[];

  const templateImages = useAppSelector(
    imagesByTemplateId(templateId)
  ) as string[];

  const loadedImages = templateId ? templateImages : organizationImages;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsLoading(true);

      const file = acceptedFiles[0];
      if (!file || !organizationId) return;

      if (file.size >= MAX_FILE_SIZE) {
        setIsLoading(false);
        openSnackbar(
          `El archivo supera el límite permitido de ${
            MAX_FILE_SIZE / ONE_MB
          }MB`,
          'error'
        );
        return;
      }

      try {
        if (templateId) {
          await dispatch(uploadTemplateImage({ templateId, file }));
        } else if (organizationId) {
          await dispatch(uploadOrganizationImage({ organizationId, file }));
        }
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, templateId]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  // ✅ checked
  const handleClose = () => {
    handleClearSelection();
    setIsOpen(false);
  };

  // ✅ checked
  const handleSelection = (image: string) => {
    if (multiple) {
      const newSelection = new Set(selectedImages);

      if (newSelection.has(image)) {
        newSelection.delete(image);
      } else {
        newSelection.add(image);
      }

      setSelectedImages([...newSelection]);
      return;
    }

    setSelectedImages([image]);
  };

  // ✅ checked
  const handleClearSelection = () => {
    setSelectedImages([]);
  };

  // ✅ checked
  const handleDelete = useCallback(async () => {
    setIsLoading(true);

    if (!organizationId) return;

    try {
      await dispatch(
        deleteOrganizationImages({ organizationId, images: selectedImages })
      );
      handleClearSelection();
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, organizationId, selectedImages]);

  const handleChange = () => {
    onChange(selectedImages);
    handleClose();
  };

  // ✅ checked
  const openMediaManager = () => {
    setIsOpen(true);
  };

  // ✅ checked
  const loadfiles = useCallback(async () => {
    if (!organizationId) return;

    try {
      setIsLoading(true);

      if (templateId) {
        await dispatch(fetchTemplateImages({ templateId }));
      } else if (organizationId) {
        await dispatch(fetchOrganizationImages({ organizationId }));
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, templateId]);

  // ✅ checked
  useEffect(() => {
    if (isOpen && !organizationImages.length && !templateImages.length) {
      loadfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, organizationImages.length]);

  return (
    <div id="media-manager">
      {multiple ? (
        <Stack direction="row" justifyContent="center">
          {hasAccess('add.media') ? (
            <Button
              variant="outlined"
              size="medium"
              fullWidth={false}
              startIcon={<UploadIcon />}
              onClick={openMediaManager}
              disabled={loadedImages.length >= MAX_FILES}
            >
              Agregar Imágenes
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Stack>
          <Stack
            flex={1}
            gap={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <ButtonBase onClick={openMediaManager}>
              <Avatar
                src={value}
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: '#DDEAFD',
                }}
                variant="rounded"
              >
                {!value ? <Trees color={palette.primary.main} /> : undefined}
              </Avatar>
            </ButtonBase>
            <Stack direction="row" alignItems="center">
              <IconButton
                onClick={openMediaManager}
                color="primary"
                disabled={loadedImages.length >= MAX_FILES}
              >
                <Upload />
              </IconButton>
              {onDelete ? (
                <IconButton onClick={onDelete} color="error">
                  <Trash2 />
                </IconButton>
              ) : null}
            </Stack>
          </Stack>
          {helperText ? (
            <FormHelperText error={!!helperText}>{helperText}</FormHelperText>
          ) : null}
        </Stack>
      )}

      <Dialog
        open={isOpen}
        maxWidth="md"
        onClose={handleClose}
        PaperProps={{
          className: 'media-manager',
        }}
        {...getRootProps({ className: 'dropzone' })}
      >
        <Stack
          className="media-manager__header"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <IconButton onClick={handleClose}>
              <ArrowLeft />
            </IconButton>
            <DialogTitle>Media</DialogTitle>
          </Stack>

          <Stack direction="row" spacing={2}>
            {hasAccess('upload.media') ? (
              <Button
                onClick={open}
                disabled={isLoading || loadedImages.length >= MAX_FILES}
                startIcon={<UploadIcon />}
                variant="text"
                autoFocus
              >
                Subir Archivo
              </Button>
            ) : null}
            {hasAccess('add.media') && selectedImages.length ? (
              <Tooltip title="Usar seleccionados" placement="top" arrow>
                <Button
                  onClick={handleChange}
                  disabled={isLoading}
                  size="medium"
                  color="success"
                  autoFocus
                >
                  Usar
                </Button>
              </Tooltip>
            ) : null}
          </Stack>
          <input {...getInputProps()} />
        </Stack>

        {isLoading ? <LinearProgress /> : null}

        <DialogContent className="media-manager__body">
          {loadedImages.length >= MAX_FILES ? (
            <Alert severity="warning" sx={{ marginBottom: 2 }}>
              Haz alcanzado el límite de archivos permitidos
            </Alert>
          ) : null}
          <Grid container rowSpacing={3} columnSpacing={2}>
            {loadedImages.map((image) => (
              <Grid
                key={image}
                size={{ xs: 6, sm: 3, md: 3 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ButtonBase
                  onClick={() => handleSelection(image)}
                  className={`media-manager__thumbnail ${
                    selectedImages.includes(image)
                      ? 'media-manager__thumbnail--selected'
                      : ''
                  }`}
                  component="div"
                >
                  <CardMedia
                    component="img"
                    width="100%"
                    height="100%"
                    image={image}
                    alt={image}
                  />
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        {selectedImages.length ? (
          <div className="media-manager__actions">
            <Stack spacing={1} direction="row" alignItems="center">
              <Tooltip title="Limpiar selección" placement="top" arrow>
                <IconButton onClick={handleClearSelection}>
                  <X />
                </IconButton>
              </Tooltip>
              <Typography variant="body1">
                {selectedImages.length}{' '}
                {pluralize('Seleccionado', selectedImages.length)}
              </Typography>
            </Stack>
            <Stack spacing={1} direction="row" alignItems="center">
              {hasAccess('delete.media') ? (
                <Tooltip title="Eliminar" placement="top" arrow>
                  <IconButton
                    onClick={handleDelete}
                    disabled={isLoading}
                    size="medium"
                    color="error"
                    autoFocus
                  >
                    <Trash2 />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default MediaManager;
